import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { BookOpen, X } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useTheme } from '../../hooks/useTheme.jsx'
import HelpTabs from './HelpTabs.jsx'
import { guidesFor } from './guideContent.js'

// Step-by-step guide — the second tab of Roles & Responsibilities.
//
// Where the first tab says what a role is for, this one shows where to click: one section per
// task, a real screenshot with red numbered marks, and a list beside it whose numbers match the
// marks. Each person sees the guide for their own role only; an Admin sees all four, since they
// are the ones who explain ProTrack to everybody else.
//
// The pictures are taken from ProTrack itself, filled with invented people and projects, by the
// script described in guideContent.js. The marks are part of the picture, so a picture and its
// list must change together.

function RoleGuide() {
  const { user } = useAuth()
  const { isDark } = useTheme()
  const [params, setParams] = useSearchParams()
  return (
    <RoleGuideView
      role={user?.role}
      isSuperAdmin={!!user?.is_super_admin}
      chosen={params.get('role')}
      onChoose={key => setParams({ role: key }, { replace: true })}
      isDark={isDark}
    />
  )
}

// Takes plain values, like RolesGuideView, so it can be shown for any role without signing in as one.
export function RoleGuideView({ role, isSuperAdmin = false, chosen, onChoose, isDark = false }) {
  const available = guidesFor(role, isSuperAdmin)
  const mine = available.find(g => g.role === role)
  const guide = available.find(g => g.key === chosen) || mine || available[0]
  const [zoom, setZoom] = useState(null)

  useEffect(() => {
    if (!zoom) return undefined
    const onKey = e => { if (e.key === 'Escape') setZoom(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [zoom])

  const c = {
    page: isDark ? 'bg-gray-900' : 'bg-gray-50',
    card: isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
    title: isDark ? 'text-white' : 'text-gray-900',
    body: isDark ? 'text-gray-300' : 'text-gray-700',
    muted: isDark ? 'text-gray-400' : 'text-gray-500',
    shot: isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50',
  }

  return (
    <div className={`min-h-full p-4 sm:p-6 ${c.page}`}>
      <div className="max-w-5xl mx-auto space-y-6">

        <header className="flex items-start gap-3">
          <div className={`p-2.5 rounded-xl ${isDark ? 'bg-gray-800 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
            <BookOpen className="w-6 h-6" aria-hidden="true" />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${c.title}`}>Roles &amp; Responsibilities</h1>
            <p className={`mt-1 text-sm max-w-3xl ${c.muted}`}>
              Where to click, and what happens when you do. The red numbers on each picture match the list
              beside it; click a picture to see it full size.
            </p>
          </div>
        </header>

        <HelpTabs active="/roles/guide" isDark={isDark} showGuide />

        {!guide && (
          <p className={`text-sm ${c.muted}`}>There is no step-by-step guide for your role yet. The Who does what tab explains what your role does.</p>
        )}

        {/* Which role's guide -- only for somebody who may see more than their own */}
        {available.length > 1 && (
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Show the guide for">
          <span className={`text-sm ${c.muted}`}>Guide for</span>
          {available.map(g => {
            const on = g.key === guide.key
            return (
              <button
                key={g.key}
                type="button"
                onClick={() => onChoose(g.key)}
                aria-pressed={on}
                className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${on
                  ? 'bg-blue-600 text-white'
                  : (isDark ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100')}`}
              >
                {g.label}{g.role === role && <span className={on ? 'ml-1.5 text-blue-100' : `ml-1.5 ${c.muted}`}>(you)</span>}
              </button>
            )
          })}
        </div>
        )}

        {guide && (<>
        <section className={`rounded-2xl border p-5 sm:p-6 ${c.card}`}>
          <h2 className={`text-lg font-semibold ${c.title}`}>How ProTrack works for the {guide.label}</h2>
          <p className={`mt-2 ${c.body}`}>{guide.intro}</p>
        </section>

        {guide.steps.map((s, i) => (
          <section key={s.img} aria-labelledby={`step-${guide.key}-${i}`} className={`rounded-2xl border p-5 sm:p-6 ${c.card}`}>
            <h2 id={`step-${guide.key}-${i}`} className={`text-lg font-semibold ${c.title}`}>{i + 1}. {s.title}</h2>
            <p className={`mt-1 text-sm ${c.muted}`}>{s.lead}</p>
            <div className="mt-4 grid gap-6 lg:grid-cols-5">
              <button
                type="button"
                onClick={() => setZoom(s)}
                className={`lg:col-span-3 self-start overflow-hidden rounded-xl border ${c.shot} cursor-zoom-in`}
                aria-label={`Show the picture for "${s.title}" full size`}
              >
                <img src={`/guide/${s.img}`} alt={s.alt} loading="lazy" className="block w-full h-auto" />
              </button>
              <div className="lg:col-span-2">
                <ol className="space-y-3">
                  {s.points.map((p, n) => (
                    <li key={p.text} className="flex gap-3">
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rose-600 text-sm font-bold text-white">{n + 1}</span>
                      <span className={c.body}>
                        {p.label && <strong className={c.title}>{p.label}</strong>}{p.label && ': '}{p.text}
                      </span>
                    </li>
                  ))}
                </ol>
                {s.note && <p className={`mt-4 text-sm ${c.muted}`}>{s.note}</p>}
              </div>
            </div>
          </section>
        ))}
        </>)}

      </div>

      {zoom && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={zoom.title}
          onClick={() => setZoom(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 cursor-zoom-out"
        >
          <button type="button" onClick={() => setZoom(null)} className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20" aria-label="Close">
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
          <img src={`/guide/${zoom.img}`} alt={zoom.alt} className="max-h-full max-w-full rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  )
}

export default RoleGuide
